import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Target,
  Lightbulb,
  TrendingUp,
  Repeat,
  PackagePlus,
  Star,
  Layers,
  DollarSign,
  Users as UsersIcon,
  Shield,
  Globe,
  Zap,
  Network,
  UserPlus,
  ClipboardList,
  Heart as HeartIcon,
  MessageSquare,
  Trophy,
  ArrowRight,
  AlertCircle,
  ChevronRight,
  Check,
  Workflow,
  Server,
  CheckCircle,
  Calendar,
  Scissors,
  Wallet,
} from "lucide-react";
import { decisionSubtypeSchema } from "@/lib/validations/onboarding";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SelectionGridSkeleton } from "./skeletons/SelectionCardSkeleton";

interface StepDecisionSubtypeProps {
  decisionType: string;
  onComplete: (subtype: string) => void;
}

interface SubtypeOption {
  title: string;
  subtitle: string;
  icon: any;
  type: string;
}

const SUBTYPE_MAP: Record<string, SubtypeOption[]> = {
  strategy: [
    { title: "Fokussierung", subtitle: "Worauf konzentrieren?", icon: Target, type: "focus" },
    { title: "Positionierung", subtitle: "Wie differenzieren?", icon: Lightbulb, type: "positioning" },
    { title: "Wachstumsrichtung", subtitle: "Wohin expandieren?", icon: TrendingUp, type: "growth_direction" },
    { title: "Pivotierung", subtitle: "Richtungswechsel nötig?", icon: Repeat, type: "pivot" },
  ],
  product: [
    { title: "Neues Produkt", subtitle: "Launch-Entscheidung", icon: PackagePlus, type: "new_product" },
    { title: "Feature-Priorisierung", subtitle: "Was bauen wir zuerst?", icon: Star, type: "feature_priority" },
    { title: "Produktstrategie", subtitle: "Roadmap & Vision", icon: Layers, type: "product_strategy" },
    { title: "Pricing", subtitle: "Preisgestaltung", icon: DollarSign, type: "pricing" },
  ],
  growth: [
    { title: "Kundenakquise", subtitle: "Neue Kunden gewinnen", icon: UsersIcon, type: "acquisition" },
    { title: "Retention", subtitle: "Kunden halten", icon: Shield, type: "retention" },
    { title: "Channel-Strategie", subtitle: "Welche Kanäle nutzen?", icon: Zap, type: "channel_strategy" },
    { title: "Internationalisierung", subtitle: "Neue Märkte erschließen", icon: Globe, type: "internationalization" },
  ],
  org_roles: [
    { title: "Teamstruktur", subtitle: "Wie organisieren?", icon: Network, type: "team_structure" },
    { title: "Neue Rolle besetzen", subtitle: "Hiring-Entscheidung", icon: UserPlus, type: "hiring" },
    { title: "Verantwortlichkeiten", subtitle: "Wer macht was?", icon: ClipboardList, type: "responsibilities" },
    { title: "Etwas anderes", subtitle: "Andere org. Frage", icon: ArrowRight, type: "other_org" },
  ],
  leadership: [
    { title: "Führungsstil", subtitle: "Wie führe ich?", icon: HeartIcon, type: "leadership_style" },
    { title: "Konfliktlösung", subtitle: "Team-Konflikte", icon: MessageSquare, type: "conflict_resolution" },
    { title: "Team-Motivation", subtitle: "Team aktivieren", icon: Trophy, type: "team_motivation" },
    { title: "Delegation", subtitle: "Was abgeben?", icon: ArrowRight, type: "delegation" },
  ],
  operations: [
    { title: "Prozessoptimierung", subtitle: "Abläufe verbessern", icon: Workflow, type: "process_optimization" },
    { title: "Systemauswahl", subtitle: "Tools & Infrastruktur", icon: Server, type: "system_selection" },
    { title: "Qualitätsmanagement", subtitle: "Standards & Kontrolle", icon: CheckCircle, type: "quality" },
    { title: "Kapazitätsplanung", subtitle: "Ressourcen steuern", icon: Calendar, type: "capacity" },
  ],
  finance: [
    { title: "Investitionsentscheidung", subtitle: "Wo investieren?", icon: TrendingUp, type: "investment" },
    { title: "Kostenoptimierung", subtitle: "Ausgaben senken", icon: Scissors, type: "cost_optimization" },
    { title: "Pricing & Marge", subtitle: "Preisgestaltung", icon: DollarSign, type: "pricing_margin" },
    { title: "Cashflow", subtitle: "Liquidität steuern", icon: Wallet, type: "cashflow" },
  ],
  other: [
    { title: "Allgemeine Entscheidung", subtitle: "Keine spezifische Kategorie", icon: ArrowRight, type: "general" },
  ],
};

export const StepDecisionSubtype = ({
  decisionType,
  onComplete,
}: StepDecisionSubtypeProps) => {
  const [selectedSubtype, setSelectedSubtype] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  const subtypes = SUBTYPE_MAP[decisionType] || SUBTYPE_MAP.other;

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 150);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = () => {
    setValidationError("");
    const validation = decisionSubtypeSchema.safeParse({
      decisionSubtype: selectedSubtype || "",
    });

    if (!validation.success) {
      const errorMessage = validation.error.errors[0]?.message || "Ungültige Eingabe";
      setValidationError(errorMessage);
      return;
    }

    if (selectedSubtype) {
      onComplete(selectedSubtype);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && selectedSubtype) {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedSubtype]);

  if (isLoading) {
    return (
      <div className="space-y-8 animate-fade-in">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
            Welche Art von Entscheidung ist das genau?
          </h1>
          <p className="mt-3 text-base text-muted-foreground">
            Das hilft mir, die richtigen Fragen zu stellen.
          </p>
        </div>
        <SelectionGridSkeleton count={4} columns={2} />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="space-y-3">
        <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
          Welche Art von Entscheidung ist das genau?
        </h1>
        <p className="text-base text-muted-foreground">
          Das hilft mir, die richtigen Fragen zu stellen.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {subtypes.map((subtype) => {
          const Icon = subtype.icon;
          const isSelected = selectedSubtype === subtype.type;
          
          return (
            <button
              key={subtype.type}
              onClick={() => setSelectedSubtype(subtype.type)}
              className={cn(
                "group relative flex items-start gap-4 rounded-xl border p-5 transition-all duration-300 min-h-[100px] text-left overflow-hidden",
                "bg-card/50 backdrop-blur-sm",
                "hover:shadow-md hover:-translate-y-0.5",
                isSelected
                  ? "border-primary bg-primary/5 shadow-[0_0_20px_hsl(var(--primary)/0.15)]"
                  : "border-border/60 hover:border-primary/40 hover:bg-card"
              )}
            >
              {/* Selection indicator */}
              <div className={cn(
                "absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-300",
                isSelected 
                  ? "border-primary bg-primary" 
                  : "border-muted-foreground/30 group-hover:border-primary/50"
              )}>
                {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
              </div>

              <div className={cn(
                "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-all duration-300",
                isSelected 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-muted/50 text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
              )}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0 pr-6">
                <p className={cn(
                  "font-semibold text-base leading-tight transition-colors duration-300",
                  isSelected ? "text-foreground" : "text-foreground/90"
                )}>
                  {subtype.title}
                </p>
                <p className="text-sm text-muted-foreground mt-1 leading-snug">
                  {subtype.subtitle}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {validationError && (
        <Alert variant="destructive" className="rounded-xl">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <Button
        onClick={handleSubmit}
        disabled={!selectedSubtype}
        className="h-12 px-8 text-base font-semibold w-full sm:w-auto gap-2 rounded-xl"
      >
        Weiter
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};
